import React, { useEffect, useMemo, useRef, useState } from "react";
import { ref, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import RichTextField from "./RichTextField";

// Default fallback terms blocks used when an offer was generated before the
// terms field existed (legacy backfill in the browser) so the editor still
// has something sensible to show. New generations populate this array
// server-side from the user's curated Terms-Bibliothek.
const DEFAULT_TERMS_BLOCKS = () => [
  {
    id: `term_${Math.random().toString(36).slice(2, 8)}`,
    title: "Partnerschaftliche Zusammenarbeit",
    html: [
      "<p>Die Zusammenarbeit kann in Absprache mit unserem Team zeitnah nach der Beauftragung beginnen.</p>",
      "<p>Sie haben jederzeit Zugriff auf unser Wissen und unsere Erfahrung. Wir arbeiten in diesem Projekt nicht zeitbasiert oder aufwandsbasiert, daher können Sie uns jederzeit kontaktieren, ohne Sorge, dass Sie zusätzliche Rechnungen erhalten.</p>",
      "<p>Im Rahmen unseres gemeinsamen Ziels werden wir dementsprechend Zeit und Ressourcen so einsetzen, um das beste Ergebnis zu erreichen. Alle Informationen, die wir während der Zusammenarbeit über Sie erhalten, behandeln wir vertraulich.</p>",
      "<p>Sie wenden den gemeinsam begonnenen Weg zu mehr Erfolg bei der Leadgenerierung und allgemeinen Zielerreichung nach bestem Wissen und Gewissen an, um den Erfolg anhand der beschriebenen Ziele zu gewährleisten. Dieses Angebot ist urheberrechtlich geschützt und unterliegt wechselseitiger Vertraulichkeit.</p>",
    ].join("\n"),
    sourceTermId: null,
  },
  {
    id: `term_${Math.random().toString(36).slice(2, 8)}`,
    title: "Zahlungsbedingungen / Gültigkeit",
    html: [
      "<p>Die monatliche Rate wird ab dem ersten Monat der Zusammenarbeit fällig. Das erste Monat dient als saubere Setup-Phase: in dieser Zeit werden Domains, Mailboxen, Warmup und die komplette Outreach-Infrastruktur aufgesetzt. Es entstehen keine zusätzlichen Setup-Kosten.</p>",
      "<p>Ab dem zweiten Monat startet die operative Outreach-Phase und wir liefern monatlich Termine gemäß dem gewählten Paket.</p>",
      "<p>Die Rechnung wird zu Beginn jedes Monats gestellt und ist innerhalb von 14 Tagen nach Rechnungsstellung zur Zahlung fällig.</p>",
    ].join("\n"),
    sourceTermId: null,
  },
];

const DEFAULT_VALID_UNTIL = () => {
  const d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
};

// Snapshot the customer-facing fields of a team member into the offer.
// We freeze them so the public offer keeps working even if the team member
// is later renamed, deleted, or has their signature swapped out. Includes
// company / legal / banking metadata so the rendered offer can show a
// proper sender "Briefkopf" footer (Geschäftsführer, Handelsregister, IBAN
// etc.) without having to re-query the live profile.
const snapshotTeamMember = (m) => {
  if (!m) return null;
  return {
    id: m.id || null,
    name: m.name || "",
    role: m.role || "",
    companyName: m.companyName || "",
    companySlogan: m.companySlogan || "",
    signature: m.signature || "",
    // Contact
    phone: m.phone || "",
    personalEmail: m.personalEmail || "",
    generalEmail: m.generalEmail || "",
    website: m.website || "",
    // Billing address
    billingStreet: m.billingStreet || "",
    billingZip: m.billingZip || "",
    billingCity: m.billingCity || "",
    billingCountry: m.billingCountry || "",
    // Legal
    ceo: m.ceo || "",
    tradeRegister: m.tradeRegister || "",
    vatId: m.vatId || "",
    taxNumber: m.taxNumber || "",
    // Banking
    bankName: m.bankName || "",
    iban: m.iban || "",
    bic: m.bic || "",
  };
};

/**
 * Apple-style display + inline editor for a generated value-based offer.
 *
 * Two modes per text node:
 *  - read mode: rendered HTML
 *  - edit mode: RichTextField with toolbar
 *
 * Click "Edit" on a section to switch into edit mode for that piece. All edits
 * propagate up via onChange so the parent can persist to RTDB.
 */

const uid = (prefix) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const formatPrice = (price, currency = "EUR") => {
  const n = Number(price) || 0;
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return `${n} ${currency}`;
  }
};

// Inline editable plain-text field (single-line). Click → input; Enter/blur → save.
const InlineText = ({ value, onChange, placeholder, className = "", multiline = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  if (editing) {
    return multiline ? (
      <textarea
        autoFocus
        className={`vbo-view__inline-input ${className}`}
        value={draft}
        rows={2}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
      />
    ) : (
      <input
        autoFocus
        className={`vbo-view__inline-input ${className}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.target.blur();
          }
        }}
      />
    );
  }

  return (
    <span
      className={`vbo-view__inline ${className}`}
      onClick={() => {
        setDraft(value || "");
        setEditing(true);
      }}
    >
      {value || <span className="vbo-view__inline-placeholder">{placeholder}</span>}
    </span>
  );
};

// Inline editable multiline text. Reads as stacked lines (recipient block,
// addresses) and turns into a focused textarea on click. Mirrors InlineText
// but preserves newlines on render so the recipient "Anschriftenfeld" reads
// like a postal address even when the value contains \n separators.
const InlineMultiline = ({
  value,
  onChange,
  placeholder,
  className = "",
  filter,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  if (editing) {
    const rowCount = Math.max(2, (draft || "").split("\n").length + 1);
    return (
      <textarea
        autoFocus
        className={`vbo-view__inline-input ${className}`}
        value={draft}
        rows={rowCount}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
          }
        }}
      />
    );
  }

  let lines = (value || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (typeof filter === "function") lines = lines.filter(filter);

  return (
    <span
      className={`vbo-view__inline ${className}`}
      onClick={() => {
        setDraft(value || "");
        setEditing(true);
      }}
    >
      {lines.length === 0 ? (
        <span className="vbo-view__inline-placeholder">{placeholder}</span>
      ) : (
        lines.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </React.Fragment>
        ))
      )}
    </span>
  );
};

// Inline editable numeric value. Renders as a formatted span (e.g. "1.640 €")
// and turns into a focused number input on click. Parses German thousand
// separators ("1.640", "1.640,5") plus standard JS numeric input. Clamps to
// [min, max] on commit.
const InlineNumber = ({
  value,
  onChange,
  format,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step,
  placeholder = "0",
  className = "",
  title,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const parseNumber = (s) => {
    if (typeof s !== "string") return Number(s) || 0;
    // Tolerate German notation: "1.640,50 €" → 1640.5
    const cleaned = s.replace(/[^\d.,-]/g, "").replace(/\./g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const commit = () => {
    const parsed = parseNumber(draft);
    const clamped = Math.max(min, Math.min(max, parsed));
    setEditing(false);
    if (clamped !== Number(value)) onChange(clamped);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        inputMode="numeric"
        className={`vbo-view__inline-input vbo-view__inline-input--num ${className}`}
        value={draft}
        title={title}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.target.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
          } else if (e.key === "ArrowUp" && step) {
            e.preventDefault();
            const next = Math.min(max, parseNumber(draft) + step);
            setDraft(String(next));
          } else if (e.key === "ArrowDown" && step) {
            e.preventDefault();
            const next = Math.max(min, parseNumber(draft) - step);
            setDraft(String(next));
          }
        }}
      />
    );
  }

  const display = format ? format(Number(value) || 0) : String(Number(value) || 0);
  return (
    <span
      className={`vbo-view__inline vbo-view__inline--num ${className}`}
      title={title || "Zum Bearbeiten klicken"}
      onClick={() => {
        setDraft(String(Number(value) || 0));
        setEditing(true);
      }}
    >
      {display || <span className="vbo-view__inline-placeholder">{placeholder}</span>}
    </span>
  );
};

// Append a fresh bullet (or paragraph) to a section's HTML. Used by the
// section-level "+ Punkt" / "+ Absatz" quick-add buttons.
const appendBulletToHtml = (html) => {
  const safe = String(html || "").trim();
  if (/<\/ul>\s*$/i.test(safe)) {
    return safe.replace(/<\/ul>\s*$/i, "<li>Neuer Punkt</li></ul>");
  }
  if (/<\/ol>\s*$/i.test(safe)) {
    return safe.replace(/<\/ol>\s*$/i, "<li>Neuer Punkt</li></ol>");
  }
  // Section currently has no list — start one. Keep any prose intact.
  return (safe ? safe + "\n" : "") + "<ul><li>Neuer Punkt</li></ul>";
};

const appendParagraphToHtml = (html) => {
  const safe = String(html || "").trim();
  return (safe ? safe + "\n" : "") + "<p>Neuer Absatz</p>";
};

const SectionEditor = ({ section, onChange, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addWrapRef = useRef(null);

  useEffect(() => {
    if (!showAddMenu) return undefined;
    const onDocMouseDown = (e) => {
      if (addWrapRef.current && !addWrapRef.current.contains(e.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showAddMenu]);

  const addBullet = () => {
    onChange({ ...section, html: appendBulletToHtml(section.html) });
    setEditing(true);
    setShowAddMenu(false);
  };
  const addParagraph = () => {
    onChange({ ...section, html: appendParagraphToHtml(section.html) });
    setEditing(true);
    setShowAddMenu(false);
  };

  return (
    <div className="vbo-view__section">
      <div className="vbo-view__section-head">
        <h2 className="vbo-view__section-title">
          <InlineText
            value={section.heading}
            placeholder="Abschnitt"
            onChange={(v) => onChange({ ...section, heading: v })}
          />
        </h2>
        <div className="vbo-view__section-actions">
          <div className="vbo-view__add-wrap" ref={addWrapRef}>
            <button
              className="vbo-view__btn-ghost vbo-view__btn-add"
              onClick={() => setShowAddMenu((s) => !s)}
              title="Eigenen Punkt oder Absatz hinzufügen"
            >
              + Hinzufügen
            </button>
            {showAddMenu && (
              <div className="vbo-view__add-menu" role="menu">
                <button
                  className="vbo-view__add-menu-item"
                  onClick={addBullet}
                >
                  <span className="vbo-view__add-menu-icon">•</span>
                  <span>
                    <strong>Bullet-Punkt</strong>
                    <em>einen weiteren Spiegelstrich anhängen</em>
                  </span>
                </button>
                <button
                  className="vbo-view__add-menu-item"
                  onClick={addParagraph}
                >
                  <span className="vbo-view__add-menu-icon">¶</span>
                  <span>
                    <strong>Freier Absatz</strong>
                    <em>für längere Erklärungen oder Zitate</em>
                  </span>
                </button>
              </div>
            )}
          </div>
          <button
            className="vbo-view__btn-ghost"
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? "Vorschau" : "Bearbeiten"}
          </button>
          {onRemove && (
            <button className="vbo-view__btn-ghost vbo-view__btn-danger" onClick={onRemove}>
              Entfernen
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <RichTextField
          value={section.html}
          onChange={(html) => onChange({ ...section, html })}
          minHeight={120}
          placeholder="Inhalt eingeben…"
        />
      ) : (
        <div
          className="vbo-view__rich"
          dangerouslySetInnerHTML={{ __html: section.html || "<em>(leer)</em>" }}
        />
      )}
    </div>
  );
};

const ModuleBlock = ({ chapter, optIdx, chpIdx, modIdx, mod, onUpdate, onRemove }) => {
  const update = (patch) => onUpdate({ ...mod, ...patch });

  return (
    <div className="vbo-view__module">
      <div className="vbo-view__module-head">
        <strong className="vbo-view__module-title">
          <InlineText
            value={mod.title}
            placeholder="Modul"
            onChange={(v) => update({ title: v })}
          />
        </strong>
        <button
          className="vbo-view__btn-remove"
          onClick={onRemove}
          title="Modul entfernen"
          aria-label="Modul entfernen"
        >
          ✕
        </button>
      </div>
      {mod.text !== undefined && (
        <div className="vbo-view__module-text">
          <RichTextField
            value={mod.text}
            onChange={(text) => update({ text })}
            placeholder="Erläuterung (optional)"
            minHeight={50}
          />
        </div>
      )}
      <ul className="vbo-view__bullets">
        {(mod.bullets || []).map((b, i) => (
          <li key={i} className="vbo-view__bullet">
            <span className="vbo-view__bullet-mark">✓</span>
            <InlineText
              value={b}
              placeholder="Bullet"
              multiline
              onChange={(v) => {
                const next = [...(mod.bullets || [])];
                next[i] = v;
                update({ bullets: next });
              }}
            />
            <button
              className="vbo-view__btn-icon"
              title="Bullet entfernen"
              onClick={() =>
                update({
                  bullets: (mod.bullets || []).filter((_, idx) => idx !== i),
                })
              }
            >
              ✕
            </button>
          </li>
        ))}
        <li>
          <button
            className="vbo-view__btn-ghost vbo-view__btn-sm"
            onClick={() => update({ bullets: [...(mod.bullets || []), ""] })}
          >
            + Bullet
          </button>
        </li>
      </ul>
    </div>
  );
};

// Editor for a single contract-terms block (title + rich-text body) inside
// the OfferView. Toggles between read-only HTML display and an inline RTF
// editor; up/down arrows reorder, ✕ removes the block.
const TermBlockEditor = ({ block, idx, total, onChange, onRemove, onMove }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className={`vbo-view__term-block ${editing ? "is-editing" : ""}`}>
      <div className="vbo-view__term-block-head">
        <strong className="vbo-view__term-block-title">
          <InlineText
            value={block.title}
            placeholder="Term-Titel"
            onChange={(v) => onChange({ title: v })}
          />
        </strong>
        <div className="vbo-view__term-block-actions">
          <button
            type="button"
            className="vbo-view__btn-icon"
            title="Nach oben"
            disabled={idx === 0}
            onClick={() => onMove(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="vbo-view__btn-icon"
            title="Nach unten"
            disabled={idx === total - 1}
            onClick={() => onMove(+1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="vbo-view__btn-ghost vbo-view__btn-sm"
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? "Vorschau" : "Bearbeiten"}
          </button>
          <button
            type="button"
            className="vbo-view__btn-remove vbo-view__btn-remove--inline"
            title="Term entfernen"
            aria-label="Term entfernen"
            onClick={onRemove}
          >
            ✕
          </button>
        </div>
      </div>
      {editing ? (
        <RichTextField
          value={block.html}
          onChange={(html) => onChange({ html })}
          placeholder="Inhalt des Terms…"
          minHeight={140}
        />
      ) : (
        <div
          className="vbo-view__rich"
          dangerouslySetInnerHTML={{
            __html: block.html || "<em>(leer)</em>",
          }}
        />
      )}
    </div>
  );
};

// "+ Term hinzufügen" affordance — collapsed pill that expands into a
// menu offering: each saved term from the library (greyed-out + disabled
// when already in the offer) plus a "Leerer Term" option for ad-hoc text.
const TermAddRow = ({
  termsLibrary,
  existingSourceIds,
  onAddBlank,
  onAddFromLibrary,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="vbo-view__term-add" ref={wrapRef}>
      <button
        type="button"
        className="vbo-view__btn-ghost"
        onClick={() => setOpen((s) => !s)}
      >
        + Term hinzufügen
      </button>
      {open && (
        <div className="vbo-view__term-add-menu" role="menu">
          {termsLibrary.length > 0 && (
            <div className="vbo-view__term-add-section">
              <div className="vbo-view__term-add-eyebrow">
                Aus Bibliothek
              </div>
              {termsLibrary.map((t) => {
                const inUse = existingSourceIds.includes(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className="vbo-view__term-add-item"
                    disabled={inUse}
                    onClick={() => {
                      onAddFromLibrary(t.id);
                      setOpen(false);
                    }}
                    title={
                      inUse
                        ? "Bereits im Angebot enthalten"
                        : "Diesen Term anhängen"
                    }
                  >
                    <span className="vbo-view__term-add-item-title">
                      {t.title}
                    </span>
                    {inUse && (
                      <span className="vbo-view__term-add-item-badge">
                        bereits enthalten
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="vbo-view__term-add-section">
            <div className="vbo-view__term-add-eyebrow">Manuell</div>
            <button
              type="button"
              className="vbo-view__term-add-item"
              onClick={() => {
                onAddBlank();
                setOpen(false);
              }}
            >
              <span className="vbo-view__term-add-item-title">
                Leerer Term
              </span>
              <span className="vbo-view__term-add-item-hint">
                Eigenen Titel + Inhalt eingeben
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const OfferView = ({ offer, onChange, onSave, onBackToConfig, onShare, isSaving, isSharing, publicUrl, userId, termsLibrary = [] }) => {
  // Ref to the rendered offer document. Used by the "Als PDF speichern"
  // button to hand the actual DOM tree (with absolute-positioned badges,
  // overlapping pills, custom fonts) to html2pdf, which captures it via
  // html2canvas and produces a multi-page A4 PDF that visually matches the
  // editor — without the awkward column re-wrap & badge clipping that the
  // browser's native `Cmd+P` print pipeline produces for this layout.
  const docRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  // Lazy-load html2pdf.js the first time the user clicks "Als PDF speichern"
  // so the (~400 KB) jsPDF/html2canvas chunk isn't shipped on every page
  // load. Adds an `.vbo-view__doc--exporting` helper class while capturing
  // so the export sees a clean read-mode version of the document (no inline
  // edit chrome, no RTF toolbars, no hover affordances).
  // Export = the browser's NATIVE print pipeline (window.print() → "Als PDF
  // speichern"), NOT html2canvas. html2canvas rasterised the whole document
  // (pixelated fonts) and clipped the 3-column tier comparison at A4 width.
  // Native print renders crisp vector text and uses the dedicated `@media
  // print` stylesheet (in ValueBasedOffer.css), which lays the tiers out
  // side-by-side, hides all editor/dashboard chrome and strips the shadow
  // artefacts — so the PDF looks exactly like the on-screen preview.
  const handleExportPdf = () => {
    if (isExporting) return;
    setIsExporting(true);
    // Defer one tick so React can paint the disabled button state before the
    // (blocking) print dialog opens, then reset once it returns.
    setTimeout(() => {
      try {
        window.print();
      } catch (e) {
        console.error("OfferView: print failed", e);
      } finally {
        setIsExporting(false);
      }
    }, 50);
  };
  // Load the salesperson's team members so the user can pick a representative
  // for the signature block. Same source as the classic OfferMaker — keeps a
  // single source of truth at /World/<uid>/team/members.
  const [teamMembers, setTeamMembers] = useState([]);
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await get(ref(database, `/World/${userId}/team/members`));
        if (!snap.exists()) {
          if (!cancelled) setTeamMembers([]);
          return;
        }
        const list = [];
        snap.forEach((child) => {
          list.push({ id: child.key, ...child.val() });
        });
        if (!cancelled) setTeamMembers(list);
      } catch (e) {
        console.error("OfferView: error loading team members", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // NOTE: ALL hooks (useMemo / useState / useEffect) MUST be declared above
  // any early return so React's hooks ordering stays stable across renders.
  // We therefore safely access `offer.generated` via optional chaining inside
  // the memoised values and only short-circuit with a render-time `null` at
  // the very end of this component.
  const g = offer?.generated || {};

  const updateGen = (patch) =>
    onChange({ ...offer, generated: { ...g, ...patch } });

  // ---------------- Recipient (Anschriftenfeld) ----------------
  // Recipient lives in `generated.recipient` once the user has touched it
  // (or once the cloud function has snapshotted it at generation time).
  // For legacy offers we fall back to the live configurator config so the
  // address block is never empty just because the field didn't exist yet
  // when the offer was originally created.
  const recipient = useMemo(() => {
    const invoiceRecipient =
      g.recipient?.invoiceRecipient ??
      offer?.config?.invoiceRecipient ??
      "";
    const legalRepresentative =
      g.recipient?.legalRepresentative ??
      offer?.config?.legalRepresentative ??
      "";
    // Addressee mode: which name appears as the single z.Hd. line. Falls
    // back to "meeting" (= invoiceRecipient) when no value was persisted —
    // matches the configurator default and the AI fallback chain.
    const addresseeMode =
      g.recipient?.addresseeMode ??
      offer?.config?.addresseeMode ??
      "meeting";
    // Resolved addressee for the single z.Hd. line: pick by mode, gracefully
    // fall back when the chosen field is empty so the letterhead never
    // renders blank just because someone toggled to a not-yet-filled name.
    const addresseeName =
      addresseeMode === "legal"
        ? legalRepresentative || invoiceRecipient
        : invoiceRecipient || legalRepresentative;
    return {
      companyName:
        g.recipient?.companyName ?? offer?.config?.companyName ?? "",
      invoiceRecipient,
      companyAddress:
        g.recipient?.companyAddress ?? offer?.config?.companyAddress ?? "",
      legalRepresentative,
      addresseeMode,
      addresseeName,
      meetingContacts: Array.isArray(g.recipient?.meetingContacts)
        ? g.recipient.meetingContacts
        : Array.isArray(offer?.config?.meetingContacts)
        ? offer.config.meetingContacts
        : [],
      // Customer-side signature override. Falls back to addresseeName /
      // companyName via the placeholder when empty so the printed PDF still
      // reads correctly without manual edits. We use `null` as the
      // "untouched" sentinel so the user can intentionally clear the field
      // and have the empty placeholder render instead of the auto-fill.
      signatureName: g.recipient?.signatureName ?? null,
      signatureCompany: g.recipient?.signatureCompany ?? null,
    };
  }, [
    g.recipient?.companyName,
    g.recipient?.invoiceRecipient,
    g.recipient?.companyAddress,
    g.recipient?.legalRepresentative,
    g.recipient?.addresseeMode,
    g.recipient?.meetingContacts,
    g.recipient?.signatureName,
    g.recipient?.signatureCompany,
    offer?.config?.companyName,
    offer?.config?.invoiceRecipient,
    offer?.config?.companyAddress,
    offer?.config?.legalRepresentative,
    offer?.config?.addresseeMode,
    offer?.config?.meetingContacts,
  ]);
  const updateRecipient = (patch) =>
    updateGen({ recipient: { ...recipient, ...patch } });

  // ---------------- Terms helpers ----------------
  // Terms are stored as an array of {id, title, html, sourceTermId?} blocks.
  // Legacy offers may have the older single-blob shape ({html, validUntil})
  // or no terms at all — in both cases we synthesise sensible defaults the
  // first time we render so the editor isn't empty.
  const terms = useMemo(() => {
    const raw = g.terms || {};
    let blocks;
    if (Array.isArray(raw.blocks)) {
      // Trust the persisted array — including the empty case (user removed
      // all blocks intentionally). We only fall back to defaults when the
      // offer has no `blocks` field at all (legacy / pre-migration shape).
      blocks = raw.blocks;
    } else if (typeof raw.html === "string" && raw.html.trim()) {
      // Legacy single-blob terms — wrap into one untitled block so the user
      // can keep editing without losing data.
      blocks = [
        {
          id: uid("term"),
          title: "Vertragsbedingungen",
          html: raw.html,
          sourceTermId: null,
        },
      ];
    } else {
      blocks = DEFAULT_TERMS_BLOCKS();
    }
    return {
      blocks,
      validUntil:
        typeof raw.validUntil === "string" && raw.validUntil.trim()
          ? raw.validUntil
          : DEFAULT_VALID_UNTIL(),
    };
  }, [g.terms]);

  const updateTerms = (patch) => updateGen({ terms: { ...terms, ...patch } });

  const updateTermBlock = (idx, patch) => {
    const next = terms.blocks.map((b, i) =>
      i === idx ? { ...b, ...patch } : b
    );
    updateTerms({ blocks: next });
  };
  const removeTermBlock = (idx) => {
    if (!window.confirm("Term entfernen?")) return;
    updateTerms({ blocks: terms.blocks.filter((_, i) => i !== idx) });
  };
  const moveTermBlock = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= terms.blocks.length) return;
    const next = [...terms.blocks];
    const [m] = next.splice(idx, 1);
    next.splice(newIdx, 0, m);
    updateTerms({ blocks: next });
  };
  const addBlankTermBlock = () => {
    updateTerms({
      blocks: [
        ...terms.blocks,
        { id: uid("term"), title: "Neuer Term", html: "<p></p>", sourceTermId: null },
      ],
    });
  };
  const addTermFromLibrary = (libraryId) => {
    const t = termsLibrary.find((x) => x.id === libraryId);
    if (!t) return;
    updateTerms({
      blocks: [
        ...terms.blocks,
        {
          id: uid("term"),
          title: t.title || "Term",
          html: t.html || "",
          sourceTermId: t.id,
        },
      ],
    });
  };

  // ---------------- Signature helpers ----------------
  const signature = useMemo(
    () => ({
      teamMemberId: g.signature?.teamMemberId || null,
      teamMember: g.signature?.teamMember || null,
      signatureMaxWidth:
        typeof g.signature?.signatureMaxWidth === "number" &&
        g.signature.signatureMaxWidth > 0
          ? g.signature.signatureMaxWidth
          : 80,
    }),
    [g.signature?.teamMemberId, g.signature?.teamMember, g.signature?.signatureMaxWidth]
  );

  // ---------------- Sender ("Rücksendeadresse") line ----------------
  // Single-line preview of the BonusClub-side sender (company + street + zip
  // city) rendered immediately above the recipient block, matching classic
  // German "Briefkopf"-typography (e.g. "wyn projects GmbH & Co. KG,
  // Stadthausbrücke 8, 20355 Hamburg" — small + underlined). Shares the
  // same enriched team-member data as the bottom footer so the document
  // stays consistent end-to-end. Returns null when no representative is
  // selected — the letterhead then falls back to the brand-only header.
  const senderLine = useMemo(() => {
    const snap = signature.teamMember;
    if (!snap) return null;
    const live =
      (snap.id && teamMembers.find((x) => x.id === snap.id)) || null;
    const m = { ...(live || {}) };
    for (const [k, v] of Object.entries(snap)) {
      if (v !== "" && v !== null && v !== undefined) m[k] = v;
    }
    // Sender line: "<Company> · <RepName> · bonusclub.io" — three short
    // pieces with a thin middot separator. Address / register details
    // stay in the bottom footer where they belong; up-top we want the
    // header to read as a clean Briefkopf, not a legal disclosure.
    const company = (m.companyName || "").trim();
    const repName = (m.name || "").trim();
    if (!company && !repName) return null;
    return { company, repName, website: "bonusclub.io" };
  }, [signature.teamMember, teamMembers]);
  const updateSignature = (patch) =>
    updateGen({ signature: { ...signature, ...patch } });

  const selectRepresentative = (memberId) => {
    if (!memberId) {
      updateSignature({ teamMemberId: null, teamMember: null });
      return;
    }
    const member = teamMembers.find((m) => m.id === memberId);
    if (!member) return;
    updateSignature({
      teamMemberId: member.id,
      teamMember: snapshotTeamMember(member),
      // If the member has its own preferred signature width, use it as a
      // sensible starting size; the user can still scale up/down per offer.
      signatureMaxWidth:
        typeof member.signatureMaxWidth === "number" && member.signatureMaxWidth > 0
          ? member.signatureMaxWidth
          : signature.signatureMaxWidth,
    });
  };


  const updateSection = (idx, next) => {
    const sections = g.sections.map((s, i) => (i === idx ? next : s));
    updateGen({ sections });
  };
  const removeSection = (idx) => {
    if (!window.confirm("Abschnitt entfernen?")) return;
    updateGen({ sections: g.sections.filter((_, i) => i !== idx) });
  };
  const addSection = () => {
    updateGen({
      sections: [
        ...g.sections,
        { id: uid("sec"), heading: "Neuer Abschnitt", html: "<p></p>" },
      ],
    });
  };

  const updateLeist = (patch) =>
    updateGen({ leistungsumfang: { ...g.leistungsumfang, ...patch } });

  // ---------------- Selected option (gewähltes Paket) ----------------
  // The admin can mark which of the (up to three) packages the prospect
  // actually picked. The chosen card gets an elegant check badge, the
  // others are visually muted ("ausgegraut"). Stored as a stable key on
  // the leistungsumfang block so both the editor and the public renderer
  // can highlight the same card. Toggling the active card clears it again.
  const optionKey = (opt, idx) => opt?.id || `idx-${idx}`;
  const selectedOptionId = g.leistungsumfang?.selectedOptionId || null;
  const toggleSelectedOption = (key) =>
    updateLeist({ selectedOptionId: selectedOptionId === key ? null : key });

  const updateOption = (i, next) => {
    const options = g.leistungsumfang.options.map((o, idx) => (idx === i ? next : o));
    updateLeist({ options });
  };

  const removeOption = (i) => {
    if (!window.confirm("Option entfernen?")) return;
    updateLeist({
      options: g.leistungsumfang.options.filter((_, idx) => idx !== i),
    });
  };

  const addOption = () => {
    if (g.leistungsumfang.options.length >= 3) return;
    updateLeist({
      options: [
        ...g.leistungsumfang.options,
        {
          id: uid("opt"),
          title: `Option ${g.leistungsumfang.options.length + 1}`,
          subtitle: "",
          price: 0,
          setupFee: 0,
          currency: "EUR",
          chapters: [],
        },
      ],
    });
  };

  const updateChapter = (optIdx, chpIdx, next) => {
    const opt = g.leistungsumfang.options[optIdx];
    const chapters = opt.chapters.map((c, i) => (i === chpIdx ? next : c));
    updateOption(optIdx, { ...opt, chapters });
  };
  const addChapter = (optIdx) => {
    const opt = g.leistungsumfang.options[optIdx];
    updateOption(optIdx, {
      ...opt,
      chapters: [
        ...opt.chapters,
        { id: uid("chp"), title: "Neues Kapitel", modules: [] },
      ],
    });
  };
  const removeChapter = (optIdx, chpIdx) => {
    if (!window.confirm("Kapitel entfernen?")) return;
    const opt = g.leistungsumfang.options[optIdx];
    updateOption(optIdx, {
      ...opt,
      chapters: opt.chapters.filter((_, i) => i !== chpIdx),
    });
  };

  const updateModule = (optIdx, chpIdx, modIdx, next) => {
    const opt = g.leistungsumfang.options[optIdx];
    const chp = opt.chapters[chpIdx];
    const modules = chp.modules.map((m, i) => (i === modIdx ? next : m));
    updateChapter(optIdx, chpIdx, { ...chp, modules });
  };
  const addModule = (optIdx, chpIdx) => {
    const opt = g.leistungsumfang.options[optIdx];
    const chp = opt.chapters[chpIdx];
    updateChapter(optIdx, chpIdx, {
      ...chp,
      modules: [
        ...chp.modules,
        { id: uid("mod"), title: "Neues Modul", text: "", bullets: [] },
      ],
    });
  };
  const removeModule = (optIdx, chpIdx, modIdx) => {
    const opt = g.leistungsumfang.options[optIdx];
    const chp = opt.chapters[chpIdx];
    updateChapter(optIdx, chpIdx, {
      ...chp,
      modules: chp.modules.filter((_, i) => i !== modIdx),
    });
  };

  // Render-time guard (placed AFTER all hooks so React sees a stable hook
  // count regardless of whether the offer payload is present yet).
  if (!offer || !offer.generated) return null;

  return (
    <div className="vbo-view">
      <div className="vbo-view__topbar">
        <button className="vbo-view__btn-ghost" onClick={onBackToConfig}>
          ← Konfigurator
        </button>
        <div className="vbo-view__topbar-actions">
          {onShare && (
            <button
              className="vbo-view__btn-ghost"
              onClick={onShare}
              disabled={isSharing || !offer.id}
              title={
                offer.id
                  ? "Öffentlichen Share-Link erzeugen und kopieren"
                  : "Bitte zuerst speichern"
              }
            >
              {isSharing ? "Teilen…" : publicUrl ? "Link kopiert ✓" : "🔗 Teilen"}
            </button>
          )}
          {/* PDF export — opens the browser's native print dialog
              (window.print → "Als PDF speichern"). This renders crisp vector
              text and obeys the dedicated `@media print` stylesheet, so the
              PDF matches the on-screen preview without the pixelation /
              clipping the old html2canvas export produced. */}
          <button
            className="vbo-view__btn-ghost"
            onClick={handleExportPdf}
            disabled={isExporting}
            title="Angebot drucken / als PDF speichern — gestochen scharf, exakt wie in der Vorschau"
          >
            {isExporting ? "Drucken…" : "📄 Als PDF"}
          </button>
          {/* Representative selector — picks the team member whose name,
              role, signature image and (legacy) sender footer details get
              snapshotted into the offer. Mirrors the select rendered inside
              the signature section so both stay in sync. Hidden until at
              least one team member exists. */}
          {teamMembers.length > 0 && (
            <select
              className="vbo-view__topbar-select"
              value={signature.teamMemberId || ""}
              onChange={(e) => selectRepresentative(e.target.value)}
              title="Repräsentant für Unterschrift & Footer auswählen"
              aria-label="Repräsentant auswählen"
            >
              <option value="">Repräsentant…</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name || "Unbenannt"}
                  {m.role ? ` — ${m.role}` : ""}
                </option>
              ))}
            </select>
          )}
          {/* Auto-save status pill. Replaces the old manual "Speichern" button:
              every inline edit silently saves after ~1.2s of inactivity. The
              pill is still clickable so the user can force a save (and as a
              fallback if auto-save ever gets out of sync). */}
          <button
            className={`vbo-view__autosave ${
              isSaving ? "is-saving" : "is-saved"
            }`}
            onClick={onSave}
            disabled={isSaving}
            title={
              isSaving
                ? "Speichert Änderungen …"
                : "Auto-Save aktiv — alle Änderungen werden automatisch gespeichert. Klick zum manuellen Speichern."
            }
          >
            <span className="vbo-view__autosave-dot" aria-hidden="true" />
            <span className="vbo-view__autosave-label">
              {isSaving ? "Speichert…" : "Gespeichert"}
            </span>
          </button>
        </div>
      </div>

      <article className="vbo-view__doc" ref={docRef}>
        {/* Letter-style head: BonusClub logo centered, recipient "Anschrift"
            block on the left below it. Mirrors a classic German "Briefkopf"
            so the offer reads like a formal proposal letter, not a webapp. */}
        <header className="vbo-view__letterhead">
          <div className="vbo-view__letterhead-brand">
            <img
              src="/img/Bonusclub_Icon.svg"
              alt="BonusClub"
              className="vbo-view__letterhead-logo"
            />
            <span className="vbo-view__letterhead-brand-name">BonusClub</span>
          </div>
          {/* Address group — bundles the small "Rücksendeadresse" sender
              line and the formal recipient (Anschriftenfeld) into ONE flex
              child so the letterhead's outer 52px gap only sits between
              the brand and this group. Inside the group sender → recipient
              are stacked tight (4px) like a real letter envelope window. */}
          <div className="vbo-view__letterhead-address">
            {senderLine && (
              <div className="vbo-view__letterhead-sender">
                {senderLine.company && (
                  <strong>{senderLine.company}</strong>
                )}
                {senderLine.company && senderLine.repName && (
                  <span
                    className="vbo-view__letterhead-sender-sep"
                    aria-hidden="true"
                  >
                    ·
                  </span>
                )}
                {senderLine.repName && (
                  <span className="vbo-view__letterhead-sender-rep">
                    {senderLine.repName}
                  </span>
                )}
                <span
                  className="vbo-view__letterhead-sender-sep"
                  aria-hidden="true"
                >
                  ·
                </span>
                <span>{senderLine.website}</span>
              </div>
            )}
          <div className="vbo-view__letterhead-recipient">
            {/* Inline-editable Anschriftenfeld. Order:
                  Firmenname (Empfänger des Schreibens)
                  z.Hd. <Empfänger> — single line. Whose name appears here
                                       is determined by `addresseeMode` in
                                       the Configurator (Hauptansprechpartner
                                       OR Geschäftsführer, never both).
                  Adresse (Straße / PLZ / Ort / Land)
                The user can click any line to refine it. Editing the z.Hd.
                line writes back into invoiceRecipient or legalRepresentative
                depending on the active addresseeMode so the data stays in
                the right place when the offer is later re-opened. */}
            <div className="vbo-view__letterhead-recipient-line vbo-view__letterhead-recipient-line--strong">
              <InlineText
                value={recipient.companyName}
                placeholder="Firmenname"
                onChange={(v) => updateRecipient({ companyName: v })}
              />
            </div>
            <div className="vbo-view__letterhead-recipient-line">
              <span className="vbo-view__letterhead-label">z.Hd.&nbsp;</span>
              <InlineText
                value={recipient.addresseeName}
                placeholder="Vor- und Nachname"
                onChange={(v) =>
                  updateRecipient(
                    recipient.addresseeMode === "legal"
                      ? { legalRepresentative: v }
                      : { invoiceRecipient: v }
                  )
                }
              />
            </div>
            <div className="vbo-view__letterhead-recipient-line vbo-view__letterhead-recipient-line--multi">
              <InlineMultiline
                value={recipient.companyAddress}
                placeholder={"Straße Nr.\n12345 Stadt\nLand"}
                onChange={(v) => updateRecipient({ companyAddress: v })}
                filter={(line) =>
                  line.toLowerCase() !==
                  String(recipient.companyName || "").trim().toLowerCase()
                }
              />
            </div>
          </div>
          </div>
        </header>

        <header className="vbo-view__hero">
          <h1 className="vbo-view__title">
            <InlineText
              value={g.title}
              placeholder="Titel des Angebots"
              onChange={(v) => updateGen({ title: v })}
            />
          </h1>
        </header>

        {/* Intro */}
        <section className="vbo-view__intro">
          <RichTextField
            value={g.introText}
            onChange={(v) => updateGen({ introText: v })}
            placeholder="Intro-Text…"
            minHeight={120}
            className="vbo-view__intro-rtf"
          />
        </section>

        {/* Sections (Situationsüberblick / Ziele / Messgrößen / Wert / ...) */}
        {g.sections.map((sec, idx) => (
          <SectionEditor
            key={sec.id || idx}
            section={sec}
            onChange={(next) => updateSection(idx, next)}
            onRemove={() => removeSection(idx)}
          />
        ))}
        <div className="vbo-view__add-row">
          <button className="vbo-view__btn-ghost" onClick={addSection}>
            + Abschnitt hinzufügen
          </button>
        </div>

        {/* Leistungsumfang */}
        <section className="vbo-view__leist">
          <div className="vbo-view__section-head">
            <h2 className="vbo-view__section-title">
              <InlineText
                value={g.leistungsumfang.heading}
                placeholder="Leistungsumfang"
                onChange={(v) => updateLeist({ heading: v })}
              />
            </h2>
          </div>
          <div className="vbo-view__leist-desc">
            <RichTextField
              value={g.leistungsumfang.description}
              onChange={(v) => updateLeist({ description: v })}
              placeholder="Einleitungstext zu den Optionen…"
              minHeight={70}
            />
          </div>

          {Array.isArray(g.leistungsumfang.setupPackages) &&
            g.leistungsumfang.setupPackages.length > 0 && (
              <div className="vbo-view__setup-section">
                <div className="vbo-view__setup-eyebrow">Setup-Pakete</div>
                <div
                  className={`vbo-view__setup-grid vbo-view__setup-grid--${g.leistungsumfang.setupPackages.length}`}
                >
                  {g.leistungsumfang.setupPackages.map((pkg, pkgIdx) => (
                    <div
                      className={`vbo-view__setup-card ${
                        pkg.price === 0 ? "is-included" : "is-addon"
                      }`}
                      key={pkg.id || pkgIdx}
                    >
                      <div className="vbo-view__setup-card-head">
                        <strong className="vbo-view__setup-card-title">
                          <InlineText
                            value={pkg.title}
                            placeholder="Setup-Paket"
                            onChange={(v) => {
                              const next = [...g.leistungsumfang.setupPackages];
                              next[pkgIdx] = { ...pkg, title: v };
                              updateLeist({ setupPackages: next });
                            }}
                          />
                        </strong>
                        <span className="vbo-view__setup-card-price">
                          {pkg.price === 0
                            ? "im Monatspreis enthalten"
                            : `${formatPrice(pkg.price, pkg.currency)} einmalig`}
                        </span>
                      </div>
                      <div className="vbo-view__setup-card-desc">
                        <RichTextField
                          value={pkg.description}
                          onChange={(v) => {
                            const next = [...g.leistungsumfang.setupPackages];
                            next[pkgIdx] = { ...pkg, description: v };
                            updateLeist({ setupPackages: next });
                          }}
                          placeholder="Beschreibung des Pakets…"
                          minHeight={48}
                        />
                      </div>
                      <ul className="vbo-view__setup-card-bullets">
                        {(pkg.bullets || []).map((b, bIdx) => (
                          <li key={bIdx}>
                            <span className="vbo-view__bullet-mark">✓</span>
                            <InlineText
                              value={b}
                              placeholder="Bullet"
                              multiline
                              onChange={(v) => {
                                const next = [
                                  ...g.leistungsumfang.setupPackages,
                                ];
                                const nextBullets = [...(pkg.bullets || [])];
                                nextBullets[bIdx] = v;
                                next[pkgIdx] = {
                                  ...pkg,
                                  bullets: nextBullets,
                                };
                                updateLeist({ setupPackages: next });
                              }}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

          <div
            className={`vbo-view__options vbo-view__options--${g.leistungsumfang.options.length}`}
          >
            {g.leistungsumfang.options.map((opt, optIdx) => {
              const optKey = optionKey(opt, optIdx);
              const isSelected = selectedOptionId === optKey;
              const isDeselected = !!selectedOptionId && !isSelected;
              return (
              <div
                className={`vbo-view__option${
                  isSelected ? " is-selected" : ""
                }${isDeselected ? " is-deselected" : ""}`}
                key={opt.id || optIdx}
              >
                {/* Auswahl-Toggle: markiert dieses Paket als das vom Kunden
                    gewählte. Klick auf das aktive Paket hebt die Auswahl
                    wieder auf. Im PDF-Export bleibt nur der aktive Toggle
                    (.is-on) als Häkchen stehen, die leeren Toggles werden
                    ausgeblendet. */}
                <button
                  type="button"
                  className={`vbo-view__option-select${
                    isSelected ? " is-on" : ""
                  }`}
                  onClick={() => toggleSelectedOption(optKey)}
                  title={
                    isSelected
                      ? "Auswahl aufheben"
                      : "Dieses Paket als gewählt markieren"
                  }
                  aria-pressed={isSelected}
                  aria-label={
                    isSelected
                      ? "Auswahl dieses Pakets aufheben"
                      : "Dieses Paket als gewählt markieren"
                  }
                >
                  <span className="vbo-view__option-select-check" aria-hidden="true">
                    ✓
                  </span>
                </button>
                {opt.recommendationBadge && !selectedOptionId && (
                  <div
                    className="vbo-view__option-badge"
                    title="Empfehlungs-Badge · klicken zum Bearbeiten · leer = ausblenden"
                  >
                    <InlineText
                      value={opt.recommendationBadge}
                      placeholder="Unsere Empfehlung"
                      onChange={(v) =>
                        updateOption(optIdx, { ...opt, recommendationBadge: v })
                      }
                    />
                  </div>
                )}
                <div className="vbo-view__option-head">
                  <div className="vbo-view__option-title">
                    <InlineText
                      value={opt.title}
                      placeholder={`Option ${optIdx + 1}`}
                      onChange={(v) => updateOption(optIdx, { ...opt, title: v })}
                    />
                  </div>
                  <div className="vbo-view__option-subtitle">
                    <InlineText
                      value={opt.subtitle}
                      placeholder="Untertitel der Option"
                      multiline
                      onChange={(v) => updateOption(optIdx, { ...opt, subtitle: v })}
                    />
                  </div>
                  <div className="vbo-view__option-price">
                    <InlineNumber
                      value={opt.price}
                      onChange={(n) =>
                        updateOption(optIdx, { ...opt, price: n })
                      }
                      format={(n) => formatPrice(n, opt.currency)}
                      min={0}
                      max={1000000}
                      step={50}
                      title="Monatspreis · klicken zum Bearbeiten"
                    />
                    {/* Per-option setup line only for legacy offers without
                        the dedicated setupPackages block. New offers surface
                        setup as a separate add-on block above the options. */}
                    {!Array.isArray(g.leistungsumfang.setupPackages) && (
                      <div
                        className={`vbo-view__option-setup ${
                          Number(opt.setupFee) > 0 ? "" : "is-zero"
                        }`}
                      >
                        + einmalig{" "}
                        <InlineNumber
                          value={opt.setupFee || 0}
                          onChange={(n) =>
                            updateOption(optIdx, { ...opt, setupFee: n })
                          }
                          format={(n) => formatPrice(n, opt.currency)}
                          min={0}
                          max={100000}
                          step={50}
                          title="Einmalige Setup-Fee · klicken zum Bearbeiten"
                        />{" "}
                        Setup
                      </div>
                    )}
                  </div>
                  <button
                    className="vbo-view__btn-remove"
                    onClick={() => removeOption(optIdx)}
                    title="Option entfernen"
                    aria-label="Option entfernen"
                  >
                    ✕
                  </button>
                </div>

                <div className="vbo-view__chapters">
                  {opt.inheritsLabel && (
                    <div
                      className="vbo-view__inherits"
                      title="Alle Inhalte der vorherigen Pakete sind hier ebenfalls enthalten — werden aber nicht erneut aufgeführt, um Platz zu sparen."
                    >
                      <span className="vbo-view__inherits-icon" aria-hidden="true">
                        ✓
                      </span>
                      <span className="vbo-view__inherits-text">
                        <InlineText
                          value={opt.inheritsLabel}
                          placeholder="Alles aus Paket Foundation"
                          onChange={(v) =>
                            updateOption(optIdx, { ...opt, inheritsLabel: v })
                          }
                        />
                      </span>
                    </div>
                  )}
                  {opt.chapters.map((chp, chpIdx) => (
                    <div className="vbo-view__chapter" key={chp.id || chpIdx}>
                      <div className="vbo-view__chapter-head">
                        <strong className="vbo-view__chapter-title">
                          <InlineText
                            value={chp.title}
                            placeholder="Kapitel"
                            onChange={(v) =>
                              updateChapter(optIdx, chpIdx, { ...chp, title: v })
                            }
                          />
                        </strong>
                        <button
                          className="vbo-view__btn-remove"
                          onClick={() => removeChapter(optIdx, chpIdx)}
                          title="Kapitel entfernen"
                          aria-label="Kapitel entfernen"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="vbo-view__modules">
                        {chp.modules.map((mod, modIdx) => (
                          <ModuleBlock
                            key={mod.id || modIdx}
                            mod={mod}
                            chapter={chp}
                            optIdx={optIdx}
                            chpIdx={chpIdx}
                            modIdx={modIdx}
                            onUpdate={(next) =>
                              updateModule(optIdx, chpIdx, modIdx, next)
                            }
                            onRemove={() => removeModule(optIdx, chpIdx, modIdx)}
                          />
                        ))}
                        <button
                          className="vbo-view__btn-ghost vbo-view__btn-sm"
                          onClick={() => addModule(optIdx, chpIdx)}
                        >
                          + Modul
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    className="vbo-view__btn-ghost"
                    onClick={() => addChapter(optIdx)}
                  >
                    + Kapitel
                  </button>
                </div>
              </div>
              );
            })}
            {g.leistungsumfang.options.length < 3 && (
              <button className="vbo-view__option vbo-view__option--add" onClick={addOption}>
                + Option hinzufügen
              </button>
            )}
          </div>
        </section>

        {/* Vertragsbedingungen */}
        <section className="vbo-view__terms">
          <div className="vbo-view__section-head">
            <h2 className="vbo-view__section-title">Vertragsbedingungen</h2>
          </div>

          <div className="vbo-view__terms-list">
            {terms.blocks.map((block, idx) => (
              <TermBlockEditor
                key={block.id || idx}
                block={block}
                idx={idx}
                total={terms.blocks.length}
                onChange={(patch) => updateTermBlock(idx, patch)}
                onRemove={() => removeTermBlock(idx)}
                onMove={(dir) => moveTermBlock(idx, dir)}
              />
            ))}
          </div>

          <TermAddRow
            termsLibrary={termsLibrary}
            existingSourceIds={terms.blocks
              .map((b) => b.sourceTermId)
              .filter(Boolean)}
            onAddBlank={addBlankTermBlock}
            onAddFromLibrary={addTermFromLibrary}
          />

          <div className="vbo-view__terms-validity">
            <span>Das Angebot ist gültig bis zum&nbsp;</span>
            <InlineText
              value={terms.validUntil}
              placeholder="DD.MM.YYYY"
              onChange={(v) => updateTerms({ validUntil: v })}
            />
          </div>
        </section>

        {/* Signature section.
            Representative selection happens in the topbar dropdown — this
            section only carries the optional signature-size slider and the
            two parallel signature cards (us / customer). The "Repräsentant"
            heading was removed because the section is purely a signature
            block, not a settings panel. */}
        <section className="vbo-view__signature">
          {signature.teamMember?.signature && (
            <div className="vbo-view__signature-controls">
              <label className="vbo-view__signature-slider">
                <span className="vbo-view__signature-label-text">
                  Unterschrift&nbsp;
                  <strong>{signature.signatureMaxWidth}px</strong>
                </span>
                <input
                  type="range"
                  min={40}
                  max={420}
                  step={10}
                  value={signature.signatureMaxWidth}
                  onChange={(e) =>
                    updateSignature({
                      signatureMaxWidth: Number(e.target.value),
                    })
                  }
                  aria-label="Unterschrift-Größe"
                  className="vbo-view__range"
                />
              </label>
            </div>
          )}

          {/* Two parallel signature cards: left = our representative,
              right = the recipient mentioned in the letterhead. The
              customer card mirrors the layout (empty signature space →
              line → printed name + company) so the offer reads as a
              proper bilateral document ready to sign on a PDF print. */}
          <div className="vbo-view__signature-pair">
            <div className="vbo-view__signature-card">
              {signature.teamMember ? (
                <>
                  {signature.teamMember.signature ? (
                    <img
                      src={signature.teamMember.signature}
                      alt={`Unterschrift ${signature.teamMember.name}`}
                      className="vbo-view__signature-image"
                      style={{ maxWidth: `${signature.signatureMaxWidth}px` }}
                    />
                  ) : (
                    <div className="vbo-view__signature-empty">
                      Keine Unterschrift im Profil hinterlegt.
                    </div>
                  )}
                  <div className="vbo-view__signature-line" />
                  <div className="vbo-view__signature-meta">
                    <div className="vbo-view__signature-name">
                      {signature.teamMember.name}
                    </div>
                    {signature.teamMember.role && (
                      <div className="vbo-view__signature-role">
                        {signature.teamMember.role}
                      </div>
                    )}
                    {signature.teamMember.companyName && (
                      <div className="vbo-view__signature-company">
                        {signature.teamMember.companyName}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="vbo-view__signature-hint">
                  Wähle oben im Topbar einen Repräsentanten aus, um
                  Unterschrift, Name, Rolle und Briefkopf-Footer
                  einzusetzen. Personen pflegst du in den Team-
                  Einstellungen unter <em>Offer Creator → Team</em>.
                </div>
              )}
            </div>

            <div className="vbo-view__signature-card vbo-view__signature-card--customer">
              <div className="vbo-view__signature-empty-space" />
              <div className="vbo-view__signature-line" />
              {/* Customer-side signature meta is editable inline so the
                  salesperson can override the pre-filled name/company in
                  edge cases (e.g. someone other than the operative
                  contact will sign on the customer's behalf). The values
                  default to recipient.invoiceRecipient / recipient.companyName
                  via the placeholder when empty. */}
              <div className="vbo-view__signature-meta">
                <InlineText
                  className="vbo-view__signature-name"
                  value={recipient.signatureName ?? recipient.addresseeName}
                  placeholder={recipient.addresseeName || "Empfänger"}
                  onChange={(v) => updateRecipient({ signatureName: v })}
                />
                <InlineText
                  className="vbo-view__signature-company"
                  value={
                    recipient.signatureCompany ?? recipient.companyName
                  }
                  placeholder={recipient.companyName || "Firma"}
                  onChange={(v) => updateRecipient({ signatureCompany: v })}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Sender footer (German "Briefkopf" at the bottom).
            Renders ONLY when a team member is selected — fields are pulled
            verbatim from /World/<uid>/team/members/<id>. NO hard-coded
            fallbacks: if no person is chosen, no footer appears.

            For legacy offers whose snapshot only stored the basic fields
            (id/name/role/companyName/signature), we transparently enrich
            with the LIVE team-member record so the footer is complete
            without requiring the user to re-select the representative. */}
        {(() => {
          const snap = signature.teamMember;
          if (!snap) return null;
          const live =
            (snap.id && teamMembers.find((x) => x.id === snap.id)) || null;
          // Merge: live data first, snapshot overrides only when its value
          // is meaningfully present. This covers BOTH legacy snapshots
          // (missing keys → live data fills in) AND new full snapshots
          // (empty strings don't blank out live data).
          const m = { ...(live || {}) };
          for (const [k, v] of Object.entries(snap)) {
            if (v !== "" && v !== null && v !== undefined) m[k] = v;
          }
          const hasCompany = !!(
            m.companyName ||
            m.billingStreet ||
            m.billingZip ||
            m.billingCity ||
            m.billingCountry ||
            m.phone
          );
          const hasLegal = !!(m.ceo || m.tradeRegister || m.vatId || m.taxNumber);
          const hasBank = !!(m.bankName || m.iban || m.bic);
          if (!hasCompany && !hasLegal && !hasBank) return null;
          return (
            <footer className="vbo-view__sender-footer">
              {hasCompany && (
                <div className="vbo-view__sender-col">
                  {m.companyName && (
                    <div className="vbo-view__sender-strong">
                      {m.companyName}
                    </div>
                  )}
                  {m.billingStreet && <div>{m.billingStreet}</div>}
                  {(m.billingZip || m.billingCity) && (
                    <div>
                      {m.billingZip} {m.billingCity}
                    </div>
                  )}
                  {m.billingCountry && <div>{m.billingCountry}</div>}
                  {m.phone && <div>TEL. {m.phone}</div>}
                </div>
              )}
              {hasLegal && (
                <div className="vbo-view__sender-col">
                  {m.ceo && (
                    <>
                      <div className="vbo-view__sender-strong">
                        Geschäftsführer:
                      </div>
                      <div>{m.ceo}</div>
                    </>
                  )}
                  {m.tradeRegister && <div>{m.tradeRegister}</div>}
                  {m.vatId && <div>USt-IdNr.: {m.vatId}</div>}
                  {m.taxNumber && <div>Steuernummer: {m.taxNumber}</div>}
                </div>
              )}
              {hasBank && (
                <div className="vbo-view__sender-col">
                  {m.bankName && (
                    <div className="vbo-view__sender-strong">{m.bankName}</div>
                  )}
                  {m.bic && <div>BIC: {m.bic}</div>}
                  {m.iban && <div>IBAN: {m.iban}</div>}
                </div>
              )}
            </footer>
          );
        })()}
      </article>
    </div>
  );
};

export default OfferView;
